import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useMediaQuery from 'hooks/useMediaQuery'
import PieChart from './components/PieChart'
import BarChart from './components/BarChart'
import { buildDemographicCharts } from './utils/chartData'
import StyledDemographicsCharts from './DemographicsCharts.style'
import * as _ from 'styles/helpers'
import { initializeDemosByCBSA, initializeDemosByCity, initializeDemosByPostalCode } from 'slices/demographicsSlice'
import { HeartPulse, Users } from 'lucide-react'

export interface Props {
	className?: string
	asset: any
}

const DemographicsCharts = ({ asset, className = '' }: Props) => {
	const dispatch = useDispatch()
	const isMobile = useMediaQuery(_.MQ.before_nav)

	const postalCode = asset.postalCode
	const uCaseCity = asset.city.toUpperCase()
	const uCaseState = asset.stateProvince.toUpperCase()

	const city = `${asset.city}`

	const { cbsa } = asset

	// @ts-ignore
	const { entity: demographic } = useSelector(state => state.demographics.byPostalCode)

	// @ts-ignore
	const { entity: demographicCity } = useSelector(state => state.demographics.byCity)

	// @ts-ignore
	const { entity: demographicCBSA } = useSelector(state => state.demographics.byCBSA)

	useEffect(() => {
		dispatch(initializeDemosByPostalCode(postalCode))
		dispatch(initializeDemosByCity({ uCaseCity, uCaseState }))
		dispatch(initializeDemosByCBSA(cbsa))
	}, [postalCode, dispatch, uCaseCity, uCaseState, cbsa])

	const [isGender, setIsGender] = useState(true)

	// The chart data is a pure function of the three areas' figures, shared
	// with presentation slides (MYD-499).
	const charts = buildDemographicCharts(
		{ postalCode: demographic, city: demographicCity, cbsa: demographicCBSA },
		{ postalCode, city, cbsa: 'CBSA' },
	)
	const populationChartData = charts.population
	const householdsChartData = charts.households
	const incomeVsPriceChartData = charts.incomeVsHomePrice
	const genderPostalCodeChartData = charts.gender.postalCode
	const genderCityCodeChartData = charts.gender.city
	const genderCbsaCodeChartData = charts.gender.cbsa
	const racePostalCodeChartData = charts.ethnicity.postalCode
	const raceCityCodeChartData = charts.ethnicity.city
	const raceCbsaCodeChartData = charts.ethnicity.cbsa

	const ChartsHeader = () => (
		<div className="DemographicsCharts__header">
			<div>
				<h1 className="text-2xl font-bold text-gray-900">Demographics</h1>
				<p className="text-sm text-gray-600 mt-1">
					Compare the postal code, city, and CBSA to understand the surrounding market.
				</p>
			</div>
		</div>
	)

	const GenderRaceToggle = () => {
		return (
			<div className="DemographicsCharts__toggle-button">
				<div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-0.5">
					<button
						type="button"
						onClick={() => setIsGender(true)}
						className={`flex items-center space-x-1 px-2.5 py-1 rounded-md transition-all text-xs ${
							isGender ? 'bg-white text-[#0080fe] shadow-sm' : 'text-gray-600 hover:text-gray-900'
						}`}
						title="View gender demographics"
					>
						<HeartPulse className="w-3 h-3" />
						<span className="font-medium">Gender</span>
					</button>
					<button
						type="button"
						onClick={() => setIsGender(false)}
						className={`flex items-center space-x-1 px-2.5 py-1 rounded-md transition-all text-xs ${
							!isGender ? 'bg-white text-[#0080fe] shadow-sm' : 'text-gray-600 hover:text-gray-900'
						}`}
						title="View ethnicity demographics"
					>
						<Users className="w-3 h-3" />
						<span className="font-medium">Ethnicity</span>
					</button>
				</div>
			</div>
		)
	}

	const ChartItem = ({ title, children }) => (
		<div>
			<h3 className="text-lg font-semibold text-gray-900 text-center pt-2">{title}</h3>
			<div className="DemographicsCharts__chart">{children}</div>
		</div>
	)

	const PieChartItem = ({ title, data }) => (
		<div className="shadow-xl cursor-pointer rounded-lg hover:scale-105 duration-100">
			<ChartItem title={title}>
				<div className="w-full h-full p-4">
					<PieChart data={data} />
				</div>
			</ChartItem>
		</div>
	)

	const BarChartItem = ({ title, data, options = {} }) => (
		<div className="shadow-xl cursor-pointer rounded-lg hover:scale-105 duration-100">
			<ChartItem title={title}>
				<div className="w-full h-full p-4">
					<BarChart options={options} data={data} />
				</div>
			</ChartItem>
		</div>
	)

	return (
		<StyledDemographicsCharts data-testid="DemographicsCharts" className={className}>
			{/* <ChartsHeader /> */}
			<div className="flex items-center justify-between mb-4">
				<ChartsHeader />
				<GenderRaceToggle />
			</div>

			{/* Mobile: Paired layout - each bar chart with its corresponding pie chart */}
			{isMobile ? (
				<div className="DemographicsCharts__paired-grid">
					<div className="DemographicsCharts__pair">
						<BarChartItem title="Population" data={populationChartData} />
						{isGender ? (
							<PieChartItem title={`Postal Code: ${postalCode}`} data={genderPostalCodeChartData} />
						) : (
							<PieChartItem title={postalCode} data={racePostalCodeChartData} />
						)}
					</div>
					<div className="DemographicsCharts__pair">
						<BarChartItem title="Households" data={householdsChartData} />
						{isGender ? (
							<PieChartItem title={city} data={genderCityCodeChartData} />
						) : (
							<PieChartItem title={city} data={raceCityCodeChartData} />
						)}
					</div>
					<div className="DemographicsCharts__pair">
						<BarChartItem title="Income vs Home Price" data={incomeVsPriceChartData} options={{ showLegend: true }} />
						{isGender ? (
							<PieChartItem title="CBSA" data={genderCbsaCodeChartData} />
						) : (
							<PieChartItem title="CBSA" data={raceCbsaCodeChartData} />
						)}
					</div>
				</div>
			) : (
				<>
					{/* Desktop: Original layout - bar charts in one row, pie charts in another */}
					<div className="DemographicsCharts__grid">
						<BarChartItem title="Population" data={populationChartData} />
						<BarChartItem title="Households" data={householdsChartData} />
						<BarChartItem title="Income vs Home Price" data={incomeVsPriceChartData} options={{ showLegend: true }} />
					</div>
					<div className="DemographicsCharts__grid">
						{isGender ? (
							<>
								<PieChartItem title={`Postal Code:  ${postalCode}`} data={genderPostalCodeChartData} />
								<PieChartItem title={city} data={genderCityCodeChartData} />
								<PieChartItem title="CBSA" data={genderCbsaCodeChartData} />
							</>
						) : (
							<>
								<PieChartItem title={postalCode} data={racePostalCodeChartData} />
								<PieChartItem title={city} data={raceCityCodeChartData} />
								<PieChartItem title="CBSA" data={raceCbsaCodeChartData} />
							</>
						)}
					</div>
				</>
			)}
		</StyledDemographicsCharts>
	)
}

export default DemographicsCharts
